/**
 * Which pane-switch clearances have been explained already — client round 4 E2.
 *
 * E2 asks for "a short note the first time each direction happens in a session". The
 * clearance itself is one store action (`switchSidebarTab`); this is the half that decides
 * whether to say anything about it, and it is a module of its own for two reasons:
 *
 * * it is **not** app state. Nothing derives from it, no permalink carries it and no view
 *   renders it — it is a fact about what this reader has already been told, which is exactly
 *   the kind of thing §F1's fixed state object should not grow a field for;
 * * it has to be testable. A flag hidden inside `Sidebar` would leak between tests in the
 *   same file, and the reset below is what stops that (`resetPaneSwitchNotices`).
 *
 * **"Session" is this page load**, not `sessionStorage` — deliberately, and unlike the gate
 * and the splash (`GATE_SEEN_KEY`). Those two are pages a reader dismisses, and showing them
 * again after a reload would be showing them again. This is a one-line explanation of why
 * something they were looking at has gone; a reader who reloads and hits the same surprise
 * has earned the same sentence. Said once per direction so that walking between the tabs does
 * not turn into a message on every click.
 */

/** Which side a switch just emptied — also the two i18n keys, by name. */
export type PaneClearance = 'indicator' | 'layers';

const explained = new Set<PaneClearance>();

/** True the first time this direction clears something; false ever after. */
export function firstPaneClearance(which: PaneClearance): boolean {
  if (explained.has(which)) return false;
  explained.add(which);
  return true;
}

/** Test helper — a fresh reader, with nothing explained yet. */
export function resetPaneSwitchNotices(): void {
  explained.clear();
}
